import { useEffect, useRef } from "react";

import { encodeRoute, isAuthCallbackHash, parseRoute, type RouteState } from "@/lib/url-state";
import { useGraph } from "@/stores/graph";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

/** Open the tab/document and focus the node described by a parsed route. */
function applyRoute(route: RouteState): void {
  const tabs = useTabs.getState();
  if (route.tab === "doc" && route.docId) {
    // The addressable single-note route. A markdown note opens in the editor, a
    // reader document in the reader — the same two surfaces the card grid and
    // the file explorer open into, so a shared link lands exactly where the
    // sender was.
    if (route.docKind === "node") tabs.openMarkdown(route.docId, "");
    else tabs.openReader(route.docId, "");
  } else if (route.tab === "reader" && route.docId) {
    tabs.openReader(route.docId, ""); // the in-pane header shows the real title
  } else if (route.tab === "ask") {
    const ask = tabs.tabs.find((t) => t.kind === "ask");
    if (ask) tabs.activate(ask.id);
    else tabs.openAsk();
  } else if (route.tab === "orbits") {
    tabs.activate("orbits");
  } else {
    tabs.activate("graph");
  }
  if (route.nodeId) useGraph.getState().requestFocus(route.nodeId);
}

/** Two-way sync between navigation state and `location.hash`. Reads an incoming
 * deep link on mount, re-applies on Back/Forward, and reflects the live view
 * back into the URL via replaceState (no history spam — the back-stack is driven
 * by overlays in useBackGuard + this popstate re-apply). */
export function useDeepLink(): void {
  const tabs = useTabs((s) => s.tabs);
  const activeId = useTabs((s) => s.activeId);
  const selection = useGraph((s) => s.selection);
  const lastHash = useRef("");
  const ready = useRef(false);

  useEffect(() => {
    const route = parseRoute(window.location.hash);
    // Only a route carrying a real target (a document, a node focus, or a
    // reader/ask tab) overrides the restored layout when the app opens. A bare
    // `#/graph` or `#/orbits` is just the previous session's echo, so the
    // persisted workspace layout stands — and a fresh install's first-run
    // default is the Atlas (opened by `workspace.bind()` when there's no
    // persisted layout), not a hash route.
    if (
      route &&
      (route.docId || route.nodeId || route.tab === "reader" || route.tab === "ask")
    ) {
      applyRoute(route);
      lastHash.current = encodeRoute(route);
    }
    ready.current = true;
    const onPop = () => {
      const next = parseRoute(window.location.hash);
      // Skip when the hash is unchanged (e.g. an overlay's sacrificial entry was
      // popped) so we don't needlessly re-focus / re-glide the camera.
      if (next && encodeRoute(next) !== lastHash.current) {
        lastHash.current = encodeRoute(next);
        applyRoute(next);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!ready.current) return;
    // NEVER overwrite a Supabase auth-callback fragment (implicit-flow OAuth /
    // email-verify / recovery return). supabase-js consumes and strips it
    // asynchronously once its chunk loads, completing sign-in; if this effect
    // replaced it with the tab route first, the minted session was lost and the
    // user bounced back to the sign-in splash. Leave the URL alone until the SDK
    // has cleared the fragment.
    if (isAuthCallbackHash(window.location.hash)) return;
    const active = tabs.find((t) => t.id === activeId);
    // Markdown notes ARE addressable now (`#/doc?id=…&kind=node`), so the URL
    // tracks them like every other surface — a note the user is looking at can
    // be copied out of the address bar and shared. This is the standing
    // requirement recorded in `noteHref`: if a user can look at it, it should be
    // reachable by URL.
    const route: RouteState =
      active?.kind === "markdown" && active.docId
        ? { tab: "doc", docId: active.docId, docKind: "node" }
        : {
            // A markdown panel with no docId can't be addressed; fall back to
            // the atlas route rather than emitting `#/markdown`, which is not a
            // route at all.
            tab: active && active.kind !== "markdown" ? active.kind : "graph",
            docId: active?.docId,
            nodeId: selection?.kind === "node" ? selection.id : undefined,
          };
    const hash = encodeRoute(route);
    if (hash !== window.location.hash) {
      window.history.replaceState(window.history.state, "", hash);
    }
    lastHash.current = hash;
  }, [tabs, activeId, selection]);
}

/** Make the system Back gesture close an open compact overlay (drawer /
 * add-sheet) instead of leaving the PWA, by pushing a sacrificial history entry
 * while an overlay is up and consuming it on close. */
export function useBackGuard(overlayOpen: boolean): void {
  useEffect(() => {
    if (!overlayOpen) return;
    // Remember the active tab when the overlay opened, so cleanup can tell
    // whether the overlay closed because the user navigated to another tab.
    const tabAtOpen = useTabs.getState().activeId;
    window.history.pushState({ gmOverlay: true }, "");
    let closedByPop = false;
    const onPop = () => {
      closedByPop = true;
      useShell.getState().closeAll();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Closed via the UI (scrim / Escape / button), not Back → remove the
      // sacrificial entry so history stays balanced. BUT if the overlay closed
      // because a different tab opened (e.g. tapping a document in a drawer
      // flips activeId), the route legitimately advanced and useDeepLink now
      // owns this entry — calling back() would navigate to the PRE-overlay route
      // and revert the new view. That revert is the recurring "Read does nothing
      // on mobile/tablet" bug (drawers exist only on compact). Skip it then.
      const state = window.history.state as { gmOverlay?: boolean } | null;
      const navigated = useTabs.getState().activeId !== tabAtOpen;
      if (!closedByPop && state?.gmOverlay && !navigated) window.history.back();
    };
  }, [overlayOpen]);
}
